<script lang="ts">
export default { name: 'CalendarDay' };
</script>

<script setup lang="ts">
import { computed, reactive, inject } from 'vue';

import type { CalendarDayParams } from './calendar-day-params';
import type { CalendarState } from './calendar-state';
import * as utils from './utils';

/**
 * `CalendarDay` component properties.
 */
export interface Props {
  index: number;
  dow: number;
  date: Date;
  itemCount: number;
}

/* eslint-disable @typescript-eslint/unified-signatures */

/**
 * `CalendarDay` component emits.
 */
export interface Emits {
  (event: 'click', eventData: MouseEvent): void;
  (event: 'dragstart', eventData: DragEvent): void;
  (event: 'drop', eventData: DragEvent): void;
  (event: 'dragover', eventData: DragEvent): void;
  (event: 'dragenter', eventData: DragEvent): void;
  (event: 'dragleave', eventData: DragEvent): void;
}

/* eslint-enable @typescript-eslint/unified-signatures */

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const calendar = reactive(inject('calendarState') as CalendarState);

const calendarDayParams = reactive(
  inject('calendarDayParams') as CalendarDayParams,
);

const classList = computed(() => {
  const isoYearMonthDay = utils.isoYearMonthDay(props.date);
  const isoMonthDay = utils.isoMonthDay(props.date);
  const paddedDay = utils.paddedDay(props.date);

  const dateClasses = calendar.dateClasses?.[isoYearMonthDay] ?? [];

  const items: string[] = [
    `dow${props.dow}`,
    `d${isoYearMonthDay}`,
    `d${isoMonthDay}`,
    `d${paddedDay}`,
    `instance${utils.instanceOfMonth(props.date)}`,
  ];

  if (utils.isSameDate(props.date, utils.today())) {
    items.push('today');
  }

  if (!utils.isSameMonth(props.date, calendar.defaultedShowDate)) {
    items.push('outside-of-month');
  }

  if (utils.isInPast(props.date)) {
    items.push('past');
  }

  if (utils.isInFuture(props.date)) {
    items.push('future');
  }

  if (utils.isLastDayOfMonth(props.date)) {
    items.push('last');
  }

  if (utils.isLastInstanceOfMonth(props.date)) {
    items.push('last-instance');
  }

  if (dayHasItems(props.date)) {
    items.push('has-items');
  }

  if (utils.isSameDate(props.date, calendar.selectionStart)) {
    items.push('selection-start');
  }

  if (utils.isSameDate(props.date, calendar.selectionEnd)) {
    items.push('selection-end');
  }

  if (props.index === 0) {
    items.push('week-start');
  }

  if (props.index === 6) {
    items.push('week-end');
  }

  if (Array.isArray(dateClasses)) {
    items.push(...dateClasses);
  } else {
    items.push(dateClasses);
  }

  return items;
});

const fomName = computed(() => {
  return calendar.showMonthNameOn1st && props.date.getDate() === 1
    ? calendar.monthNames[props.date.getMonth()]
    : '';
});

const dateLabel = computed(() => {
  return props.date.getDate().toString();
});

const dropEffect = computed(() => {
  let effect: 'move' | 'execute' | 'none';

  if (calendar.enableDragDrop && calendar.currentDragItem) {
    effect = 'move';
  } else if (calendar.enableDateSelection && calendar.dateSelectionOrigin) {
    effect = 'execute';
  } else {
    effect = 'none';
  }

  return effect;
});

const isSelected = computed(() => {
  if (!calendar.selectionStart || !calendar.selectionEnd) return false;

  if (props.date < utils.dateOnly(calendar.selectionStart)) return false;

  if (props.date > utils.dateOnly(calendar.selectionEnd)) return false;

  return true;
});

const isDraggable = computed(() => {
  return calendar.enableDateSelection;
});

const isGrabbed = computed(() => {
  return isDraggable.value ? isSelected.value : false;
});

const itemsOverflow = computed(() => {
  if (props.itemCount <= calendarDayParams.maxDisplayable) {
    return null;
  }

  const count = props.itemCount - calendarDayParams.maxDisplayable;
  const top =
    calendarDayParams.topOffset +
    (props.itemCount - count) * calendarDayParams.itemHeight;

  return { count, top };
});

//#region Helper Functions

function dayHasItems(day: Date) {
  return !!calendar.fixedItems.find(
    (d) => d.endDate >= day && utils.dateOnly(d.startDate) <= day,
  );
}

//#endregion Helper Functions
</script>

<template>
  <div
    :class="['calendar-day', ...classList]"
    :draggable="isDraggable"
    :aria-grabbed="isGrabbed"
    :aria-label="dateLabel"
    :aria-selected="isSelected"
    :aria-dropeffect="dropEffect"
    @click="emit('click', $event)"
    @dragstart="emit('dragstart', $event)"
    @drop="emit('drop', $event)"
    @dragover="emit('dragover', $event)"
    @dragenter="emit('dragenter', $event)"
    @dragleave="emit('dragleave', $event)"
  >
    <div class="calendar-day-number">
      <span v-if="fomName" class="calendar-fom-name">{{ fomName }}</span>
      <span class="calendar-day-number-text">{{ date.getDate() }}</span>
    </div>

    <slot name="day-content" />

    <div
      v-if="itemsOverflow"
      class="calendar-items-overflow-notice"
      :style="{ top: `${itemsOverflow.top}px` }"
    >
      + {{ itemsOverflow.count }} more
    </div>
  </div>
</template>

<style scoped lang="scss">
.calendar-day {
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 0;
  position: sticky;
  top: 0;
  direction: initial;
  overflow: hidden;
  border: none !important;

  // &.week-start, &.week-end { background-color: #2a2b33;
  // }

  &[draggable] {
    user-select: none;
  }

  @include app-theme-light {
    background-color: #f1f5ff;
  }

  @include app-theme-dark {
    background-color: #1e2027;
  }
}

.calendar-day-number {
  height: 45px;
  width: 100%;
  padding: 0.2em;
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .d01 &:has(.calendar-fom-name) {
    background-color: #ffffff0d;
  }

  /* Allows emoji icons or labels (such as holidays) to be added more easily to
specific dates by having the margin set already. */
  ::before {
    margin-right: 0.5em;
  }

  /* Default styling for holiday hover descriptions */
  &:hover::after {
    position: absolute;
    top: 1rem;
    padding: 0.2rem;
    margin: 0.5rem;
    line-height: 1.2;
    background-color: var(--cal-holiday-bg, #f7f7f7);
    border: var(--cal-holiday-border, 1px solid #f0f0f0);
    box-shadow: 0.1rem 0.1rem 0.2rem
      var(--cal-holiday-shadow, rgba(0, 0, 0, 0.25));
  }
}

.calendar-day-number-text {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px !important;
  border-radius: 15px;

  .calendar-day.today & {
    color: white;
    background-color: var(--red);
  }
}

.calendar-items-overflow-notice {
  position: absolute;
  bottom: 4px;
  left: 0;
  right: 0;
  color: var(--text-color);
  font-size: 1rem !important;
  line-height: 1rem;
  padding-left: 8px;
  padding-top: 4px;
}
</style>
